import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Brain, BookOpen, FileText, Send, Users, User, MessageSquare, 
  Download, Mail, Phone, Loader2, CheckCircle, AlertCircle,
  ChevronLeft, ChevronRight, Plus, Save, Trash2, Eye, Settings,
  BarChart3, TrendingUp, Clock, Calendar, Mic, Upload, Volume2,
  CloudUpload, ExternalLink, Edit, Link, UsersRound, Printer, Wand2
} from 'lucide-react';
import api from '../lib/api';
import FerramentasIA from '../components/FerramentasIA';

// Níveis de fluência WCPM
const WCPM_NIVEIS = {
  1: { nome: "Crítico", cor: "bg-red-500", descricao: "0-40 WCPM" },
  2: { nome: "Em desenvolvimento", cor: "bg-yellow-500", descricao: "41-70 WCPM" },
  3: { nome: "Adequado", cor: "bg-blue-500", descricao: "71-100 WCPM" },
  4: { nome: "Avançado", cor: "bg-green-500", descricao: "101+ WCPM" }
};

const DnaIA = () => {
  // Estados principais
  const [activeTab, setActiveTab] = useState('aplicacoes');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  
  // Filtros
  const [selectedTurma, setSelectedTurma] = useState('all');
  const [selectedIdioma, setSelectedIdioma] = useState('portugues');
  const [turmas, setTurmas] = useState([]);
  const [students, setStudents] = useState([]);
  
  // Aplicações de leitura
  const [aplicacoes, setAplicacoes] = useState([]);
  const [selectedAplicacao, setSelectedAplicacao] = useState(null);
  
  // Upload de áudio
  const audioInputRef = useRef(null);
  const audioInputTurmaRef = useRef(null);
  const [showUploadTurma, setShowUploadTurma] = useState(false);
  const [uploadTurmaConfig, setUploadTurmaConfig] = useState({
    turma_id: '',
    texto_original: '',
    idioma: 'portugues',
    data_aplicacao: new Date().toISOString().split('T')[0]
  });
  
  // Modal de nova aplicação
  const [showNovaAplicacao, setShowNovaAplicacao] = useState(false);
  const [novaAplicacao, setNovaAplicacao] = useState({
    student_id: '',
    student_name: '',
    turma_id: '',
    turma_name: '',
    idioma: 'portugues',
    texto_original: '',
    texto_lido: '',
    tempo_leitura: '',
    tempo_segundos: 60,
    data_aplicacao: new Date().toISOString().split('T')[0],
    ano_escolar: '',
    professor_name: '',
    audio_url: '',
    selecionar_todos: false
  });
  
  // Modal de edição
  const [showEditarAplicacao, setShowEditarAplicacao] = useState(false);
  const [editAplicacao, setEditAplicacao] = useState(null);
  
  // Modal de análise IA
  const [showAnalise, setShowAnalise] = useState(false);
  const [analiseResult, setAnaliseResult] = useState(null);
  
  // Modal de registro técnico (visualização)
  const [showRegistroTecnico, setShowRegistroTecnico] = useState(false);
  
  // Modal de envio
  const [showEnvio, setShowEnvio] = useState(false);
  const [envioConfig, setEnvioConfig] = useState({
    tipo: 'individual',
    metodo: 'email',
    destinatarios: []
  });
  
  // Configurações da API
  const [showConfig, setShowConfig] = useState(false);
  const [apiKeyConfig, setApiKeyConfig] = useState('');
  
  // Google Drive
  const [driveConnected, setDriveConnected] = useState(false);
  
  // Dashboard de Evolução
  const [evolucaoData, setEvolucaoData] = useState(null);
  const [selectedTrimestreEvolucao, setSelectedTrimestreEvolucao] = useState('all');
  const [selectedStudentEvolucao, setSelectedStudentEvolucao] = useState('all');
  const [loadingEvolucao, setLoadingEvolucao] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  
  // Modal Criar Texto
  const [showCriarTexto, setShowCriarTexto] = useState(false);
  const [gerandoTexto, setGerandoTexto] = useState(false);
  const [textoGerado, setTextoGerado] = useState(null);
  const [criarTextoConfig, setCriarTextoConfig] = useState({
    ano_escolar: '',
    periodo: 'meio',
    genero_textual: 'narrativo',
    tema: '',
    idioma: 'portugues'
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadTurmas();
    loadAplicacoes();
    checkDriveStatus();
  }, []);

  useEffect(() => {
    if (selectedTurma !== 'all') {
      loadStudentsByTurma(selectedTurma);
    }
  }, [selectedTurma]);

  const checkDriveStatus = async () => {
    try {
      const res = await api.get('/drive/status');
      setDriveConnected(res.data.connected);
    } catch (error) {
      console.error('Erro ao verificar status do Drive:', error);
    }
  };

  const handleConnectDrive = async () => {
    try {
      const res = await api.get('/drive/connect');
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      toast.error('Erro ao conectar com Google Drive. Configure as credenciais primeiro.');
    }
  };

  const handleSaveToDrive = async (aplicacaoId) => {
    setSavingToDrive(true);
    try {
      const res = await api.post('/dna-ia/salvar-drive', {
        aplicacao_id: aplicacaoId
      });
      
      if (res.data.success) {
        toast.success('Análise salva no Google Drive!');
        if (res.data.drive_link) {
          window.open(res.data.drive_link, '_blank');
        }
        loadAplicacoes(); // Recarregar para atualizar status
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('não conectado')) {
        toast.error('Conecte o Google Drive primeiro');
      } else {
        toast.error('Erro ao salvar no Drive');
      }
    } finally {
      setSavingToDrive(false);
    }
  };

  const loadTurmas = async () => {
    try {
      const res = await api.get('/class-groups');
      setTurmas(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const loadEvolucaoData = async () => {
    setLoadingEvolucao(true);
    try {
      const params = new URLSearchParams();
      if (selectedTurma !== 'all') params.append('turma_id', selectedTurma);
      if (selectedStudentEvolucao !== 'all') params.append('student_id', selectedStudentEvolucao);
      if (selectedTrimestreEvolucao !== 'all') params.append('trimestre', selectedTrimestreEvolucao);
      
      const res = await api.get(`/dna-ia/evolucao?${params.toString()}`);
      setEvolucaoData(res.data);
    } catch (error) {
      console.error('Erro ao carregar dados de evolução:', error);
      toast.error('Erro ao carregar dados de evolução');
    } finally {
      setLoadingEvolucao(false);
    }
  };

  // Carregar evolução quando mudar filtros ou aba
  useEffect(() => {
    if (activeTab === 'evolucao') {
      loadEvolucaoData();
    }
  }, [activeTab, selectedTurma, selectedTrimestreEvolucao, selectedStudentEvolucao]);

  const loadStudentsByTurma = async (turmaId) => {
    try {
      const res = await api.get(`/students?class_id=${turmaId}`);
      // Filtrar apenas alunos ativos
      const alunosAtivos = (res.data || []).filter(s => s.situation === 'Ativo' || s.active !== false);
      setStudents(alunosAtivos);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  };

  const loadAplicacoes = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dna-ia/aplicacoes');
      setAplicacoes(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar aplicações:', error);
      // Dados de exemplo se não houver no banco
      setAplicacoes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNovaAplicacao = async () => {
    if (!novaAplicacao.texto_original) {
      toast.error('Preencha o texto original');
      return;
    }
    
    if (!novaAplicacao.selecionar_todos && !novaAplicacao.student_id) {
      toast.error('Selecione um aluno ou marque "Todos os alunos"');
      return;
    }
    
    setLoading(true);
    try {
      if (novaAplicacao.selecionar_todos && novaAplicacao.turma_id) {
        // Criar aplicação para todos os alunos da turma
        let criados = 0;
        for (const student of students) {
          await api.post('/dna-ia/aplicacoes', {
            ...novaAplicacao,
            student_id: student.id,
            student_name: student.name || student.full_name,
            selecionar_todos: undefined
          });
          criados++;
        }
        toast.success(`${criados} aplicações criadas com sucesso!`);
      } else {
        // Criar para um aluno específico
        await api.post('/dna-ia/aplicacoes', {
          ...novaAplicacao,
          selecionar_todos: undefined
        });
        toast.success('Aplicação registrada com sucesso!');
      }
      
      setShowNovaAplicacao(false);
      setNovaAplicacao({
        student_id: '',
        student_name: '',
        turma_id: '',
        turma_name: '',
        idioma: 'portugues',
        texto_original: '',
        texto_lido: '',
        tempo_leitura: '',
        tempo_segundos: 60,
        data_aplicacao: new Date().toISOString().split('T')[0],
        ano_escolar: '',
        professor_name: '',
        audio_url: '',
        selecionar_todos: false
      });
      loadAplicacoes();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar aplicação: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Editar aplicação
  const handleEditarAplicacao = async () => {
    if (!editAplicacao) return;
    
    setLoading(true);
    try {
      await api.put(`/dna-ia/aplicacoes/${editAplicacao.id}`, editAplicacao);
      toast.success('Aplicação atualizada!');
      setShowEditarAplicacao(false);
      setEditAplicacao(null);
      loadAplicacoes();
    } catch (error) {
      toast.error('Erro ao atualizar aplicação');
    } finally {
      setLoading(false);
    }
  };

  // Excluir aplicação
  const handleExcluirAplicacao = async (aplicacaoId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta aplicação?')) return;
    
    try {
      await api.delete(`/dna-ia/aplicacoes/${aplicacaoId}`);
      toast.success('Aplicação excluída!');
      loadAplicacoes();
    } catch (error) {
      toast.error('Erro ao excluir aplicação');
    }
  };

  const handleAnalisarIA = async (aplicacao) => {
    setSelectedAplicacao(aplicacao);
    setShowAnalise(true);
    
    // Se já tem análise salva, usar ela
    if (aplicacao.analise_ia && Object.keys(aplicacao.analise_ia).length > 0) {
      setAnaliseResult(aplicacao.analise_ia);
      setAnalyzing(false);
      return;
    }
    
    // Caso contrário, fazer nova análise
    setAnalyzing(true);
    
    try {
      const res = await api.post('/dna-ia/analisar', {
        aplicacao_id: aplicacao.id,
        texto_original: aplicacao.texto_original,
        texto_lido: aplicacao.texto_lido,
        idioma: aplicacao.idioma,
        tempo_leitura: aplicacao.tempo_leitura,
        tempo_segundos: aplicacao.tempo_segundos,
        student_name: aplicacao.student_name,
        turma_name: aplicacao.turma_name,
        ano_escolar: aplicacao.ano_escolar,
        professor_name: aplicacao.professor_name
      });
      
      setAnaliseResult(res.data);
      toast.success('Análise concluída!');
    } catch (error) {
      toast.error('Erro ao analisar com IA');
      setAnaliseResult(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSalvarAnalise = async () => {
    if (!analiseResult || !selectedAplicacao) return;
    
    try {
      await api.put(`/dna-ia/aplicacoes/${selectedAplicacao.id}`, {
        ...selectedAplicacao,
        analise_ia: analiseResult
      });
      toast.success('Análise salva com sucesso!');
      loadAplicacoes();
      setShowAnalise(false);
    } catch (error) {
      toast.error('Erro ao salvar análise');
    }
  };

  const handleSalvarRegistroTecnico = async () => {
    // Registro técnico agora é gerado pela IA, apenas visualização
    setShowRegistroTecnico(false);
  };

  // Transcrever áudio individual
  const handleTranscreverAudio = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('student_name', novaAplicacao.student_name || '');
      formData.append('turma_id', novaAplicacao.turma_id || '');
      
      const res = await api.post('/dna-ia/transcrever-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setNovaAplicacao(prev => ({
          ...prev,
          texto_lido: res.data.transcricao
        }));
        toast.success('Áudio transcrito com sucesso!');
      }
    } catch (error) {
      toast.error('Erro ao transcrever áudio');
    } finally {
      setTranscribing(false);
    }
  };

  // Upload de múltiplos áudios por turma
  const handleUploadAudiosTurma = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (!uploadTurmaConfig.turma_id || !uploadTurmaConfig.texto_original) {
      toast.error('Selecione a turma e informe o texto original');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('audios', file));
      formData.append('turma_id', uploadTurmaConfig.turma_id);
      formData.append('texto_original', uploadTurmaConfig.texto_original);
      formData.append('idioma', uploadTurmaConfig.idioma);
      formData.append('data_aplicacao', uploadTurmaConfig.data_aplicacao);
      
      const res = await api.post('/dna-ia/upload-audios-turma', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${res.data.sucesso} áudios transcritos de ${res.data.total}!`);
      if (res.data.erros > 0) {
        toast.warning(`${res.data.erros} áudios com erro`);
      }
      
      setShowUploadTurma(false);
      loadAplicacoes();
    } catch (error) {
      toast.error('Erro ao processar áudios');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarPDF = async (aplicacao) => {
    try {
      const res = await api.get(`/dna-ia/relatorio-pdf/${aplicacao.id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_fluencia_${aplicacao.student_name}_${aplicacao.data_aplicacao}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleGerarTodosPDFs = async () => {
    const aplicacoesFiltradas = aplicacoes.filter(a => 
      selectedTurma === 'all' || a.turma_id === selectedTurma
    );
    
    if (aplicacoesFiltradas.length === 0) {
      toast.error('Nenhuma aplicação encontrada');
      return;
    }
    
    setLoading(true);
    try {
      for (const aplicacao of aplicacoesFiltradas) {
        await handleGerarPDF(aplicacao);
      }
      toast.success(`${aplicacoesFiltradas.length} PDFs gerados!`);
    } catch (error) {
      toast.error('Erro ao gerar PDFs');
    } finally {
      setLoading(false);
    }
  };

  const handleEnviar = async () => {
    if (envioConfig.destinatarios.length === 0) {
      toast.error('Selecione os destinatários');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/dna-ia/enviar-relatorios', {
        ...envioConfig,
        aplicacoes: envioConfig.tipo === 'individual' 
          ? [selectedAplicacao.id] 
          : aplicacoes.filter(a => a.turma_id === selectedTurma).map(a => a.id)
      });
      
      toast.success(`Relatórios enviados por ${envioConfig.metodo === 'email' ? 'email' : 'WhatsApp'}!`);
      setShowEnvio(false);
    } catch (error) {
      toast.error('Erro ao enviar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarApiKey = async () => {
    try {
      await api.post('/dna-ia/config', { openai_api_key: apiKeyConfig });
      toast.success('Chave API salva com sucesso!');
      setShowConfig(false);
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const handleGerarTexto = async () => {
    if (!criarTextoConfig.ano_escolar) {
      toast.error('Selecione o ano escolar');
      return;
    }
    
    setGerandoTexto(true);
    setTextoGerado(null);
    try {
      const res = await api.post('/dna-ia/gerar-texto', criarTextoConfig);
      setTextoGerado(res.data);
      toast.success(`Texto gerado com ${res.data.palavras} palavras!`);
    } catch (error) {
      toast.error('Erro ao gerar texto');
    } finally {
      setGerandoTexto(false);
    }
  };

  const handleUsarTextoGerado = () => {
    if (textoGerado?.texto) {
      setNovaAplicacao(prev => ({
        ...prev,
        texto_original: textoGerado.texto,
        idioma: textoGerado.idioma || 'portugues'
      }));
      setShowCriarTexto(false);
      setShowNovaAplicacao(true);
      toast.success('Texto inserido na nova aplicação!');
    }
  };

  const handleCopiarTexto = () => {
    if (textoGerado?.texto) {
      navigator.clipboard.writeText(textoGerado.texto);
      toast.success('Texto copiado!');
    }
  };

  const handleImprimirTexto = () => {
    if (!textoGerado?.texto) return;
    
    const periodos = { inicio: 'Início do Ano', meio: 'Meio do Ano', fim: 'Fim do Ano' };
    const generos = {
      narrativo: 'Narrativo (História)',
      fabula: 'Fábula',
      informativo: 'Informativo',
      descritivo: 'Descritivo',
      poesia: 'Poesia',
      instrucional: 'Instrucional'
    };
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
      <head>
        <title>Texto DNA.IA - ${textoGerado.ano_escolar}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.8; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #7B3F98; padding-bottom: 20px; }
          .header h1 { color: #7B3F98; margin: 0; }
          .info { display: flex; justify-content: space-between; margin-bottom: 20px; padding: 15px; background: #f3f4f6; border-radius: 8px; }
          .info-item { text-align: center; }
          .info-label { font-size: 12px; color: #6b7280; }
          .info-value { font-weight: bold; color: #7B3F98; }
          .texto { font-size: 18px; line-height: 2; padding: 20px; border: 2px solid #e5e7eb; border-radius: 8px; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #9ca3af; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📚 DNA.IA - Texto para Avaliação de Fluência</h1>
          <p>Sistema de Avaliação de Fluência de Leitura</p>
        </div>
        <div class="info">
          <div class="info-item">
            <div class="info-label">Ano Escolar</div>
            <div class="info-value">${textoGerado.ano_escolar}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Período</div>
            <div class="info-value">${periodos[textoGerado.periodo] || textoGerado.periodo}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Gênero</div>
            <div class="info-value">${generos[textoGerado.genero] || textoGerado.genero}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Meta WCPM</div>
            <div class="info-value">${textoGerado.wcpm_alvo}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Total Palavras</div>
            <div class="info-value">${textoGerado.palavras}</div>
          </div>
        </div>
        <div class="texto">
          ${textoGerado.texto}
        </div>
        <div class="footer">
          <p>Texto gerado pelo DNA.IA para leitura em aproximadamente 60 segundos</p>
          <p>DNACore - Sistema de Gestão Escolar</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleEnviarTextoEmail = async () => {
    if (!textoGerado) return;
    
    setLoading(true);
    try {
      const res = await api.post('/dna-ia/enviar-texto-email', textoGerado);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        // SMTP não configurado - abrir cliente de email
        const subject = encodeURIComponent(`📚 Texto DNA.IA - ${textoGerado.ano_escolar} - ${textoGerado.palavras} palavras`);
        const body = encodeURIComponent(`Texto para Avaliação de Fluência de Leitura

Parâmetros:
- Ano Escolar: ${textoGerado.ano_escolar}
- Período: ${textoGerado.periodo}
- Gênero: ${textoGerado.genero}
- Meta WCPM: ${textoGerado.wcpm_alvo} palavras/minuto
- Total de palavras: ${textoGerado.palavras}

TEXTO PARA LEITURA:

${textoGerado.texto}

---
DNA.IA - Sistema de Avaliação de Fluência de Leitura | DNACore`);
        
        window.open(`mailto:${res.data.email_destino || ''}?subject=${subject}&body=${body}`, '_blank');
        toast.info('Abrindo cliente de email...');
      }
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  const getIdiomaLabel = (idioma) => {
    const labels = {
      portugues: 'Português',
      ingles: 'Inglês',
      espanhol: 'Espanhol'
    };
    return labels[idioma] || idioma;
  };

  const getNivelFluencia = (score) => {
    if (score >= 90) return { label: 'Excelente', color: 'bg-green-500' };
    if (score >= 70) return { label: 'Bom', color: 'bg-blue-500' };
    if (score >= 50) return { label: 'Regular', color: 'bg-yellow-500' };
    return { label: 'Precisa Melhorar', color: 'bg-red-500' };
  };

  const aplicacoesFiltradas = aplicacoes.filter(a => {
    if (selectedTurma !== 'all' && a.turma_id !== selectedTurma) return false;
    if (a.idioma !== selectedIdioma) return false;
    return true;
  });

  // Estatísticas
  const stats = {
    total: aplicacoesFiltradas.length,
    analisadas: aplicacoesFiltradas.filter(a => a.analise_ia).length,
    mediaFluencia: aplicacoesFiltradas.length > 0 
      ? Math.round(aplicacoesFiltradas.reduce((acc, a) => acc + (a.analise_ia?.score_fluencia || 0), 0) / aplicacoesFiltradas.length)
      : 0
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">DNA.IA</h1>
            <p className="text-muted-foreground">Avaliação de Fluência de Leitura com Inteligência Artificial</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {driveConnected ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="w-3 h-3 mr-1" />
              Drive Conectado
            </Badge>
          ) : (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleConnectDrive}
              className="bg-blue-50 border-blue-200 hover:bg-blue-100"
            >
              <CloudUpload className="w-4 h-4 mr-2 text-blue-600" />
              Conectar Drive
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowConfig(true)} data-testid="config-btn">
            <Settings className="w-4 h-4 mr-2" />
            Configurações
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label>Turma:</Label>
              <Select value={selectedTurma} onValueChange={setSelectedTurma}>
                <SelectTrigger className="w-48" data-testid="select-turma">
                  <SelectValue placeholder="Todas as turmas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as turmas</SelectItem>
                  {turmas.map(turma => (
                    <SelectItem key={turma.id} value={turma.id}>
                      {turma.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label>Idioma:</Label>
              <Select value={selectedIdioma} onValueChange={setSelectedIdioma}>
                <SelectTrigger className="w-40" data-testid="select-idioma">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portugues">🇧🇷 Português</SelectItem>
                  <SelectItem value="ingles">🇺🇸 Inglês</SelectItem>
                  <SelectItem value="espanhol">🇪🇸 Espanhol</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setTextoGerado(null);
                  setCriarTextoConfig({
                    ano_escolar: '',
                    periodo: 'meio',
                    genero_textual: 'narrativo',
                    tema: '',
                    idioma: 'portugues'
                  });
                  setShowCriarTexto(true);
                }} 
                data-testid="criar-texto-btn"
                className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200 hover:from-purple-100 hover:to-violet-100"
              >
                <Brain className="w-4 h-4 mr-2 text-purple-600" />
                Criar Texto com IA
              </Button>
              <Button onClick={() => setShowNovaAplicacao(true)} data-testid="nova-aplicacao-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nova Aplicação
              </Button>
              <Button variant="outline" onClick={handleGerarTodosPDFs} disabled={loading}>
                <Download className="w-4 h-4 mr-2" />
                Gerar Todos PDFs
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total de Aplicações</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Analisadas por IA</p>
                <p className="text-2xl font-bold">{stats.analisadas}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Média de Fluência</p>
                <p className="text-2xl font-bold">{stats.mediaFluencia}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold">{stats.total - stats.analisadas}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="aplicacoes" data-testid="tab-aplicacoes">
            <BookOpen className="w-4 h-4 mr-2" />
            Aplicações
          </TabsTrigger>
          <TabsTrigger value="evolucao" data-testid="tab-evolucao">
            <TrendingUp className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="painel" data-testid="tab-painel">
            <BarChart3 className="w-4 h-4 mr-2" />
            Painel
          </TabsTrigger>
        </TabsList>

        {/* Tab Aplicações */}
        <TabsContent value="aplicacoes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Aplicações de Fluência de Leitura</CardTitle>
              <CardDescription>
                Gerencie as avaliações de leitura dos alunos em {getIdiomaLabel(selectedIdioma)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : aplicacoesFiltradas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma aplicação encontrada</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowNovaAplicacao(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar primeira aplicação
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aluno</TableHead>
                      <TableHead>Turma</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Tempo</TableHead>
                      <TableHead>Fluência</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aplicacoesFiltradas.map(aplicacao => {
                      const nivel = aplicacao.analise_ia 
                        ? getNivelFluencia(aplicacao.analise_ia.score_fluencia) 
                        : null;
                      
                      return (
                        <TableRow key={aplicacao.id}>
                          <TableCell className="font-medium">{aplicacao.student_name}</TableCell>
                          <TableCell>{aplicacao.turma_name || '-'}</TableCell>
                          <TableCell>{new Date(aplicacao.data_aplicacao).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>{aplicacao.tempo_leitura || '-'}</TableCell>
                          <TableCell>
                            {nivel ? (
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${nivel.color}`} />
                                <span>{aplicacao.analise_ia.score_fluencia}%</span>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {aplicacao.analise_ia ? (
                              <Badge variant="success" className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Analisada
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Pendente
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleAnalisarIA(aplicacao)}
                                title="Analisar com IA"
                                data-testid={`analisar-${aplicacao.id}`}
                              >
                                <Brain className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setSelectedAplicacao(aplicacao);
                                  setShowRegistroTecnico(true);
                                }}
                                title="Registro Técnico"
                                data-testid={`registro-${aplicacao.id}`}
                              >
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleGerarPDF(aplicacao)}
                                title="Gerar PDF"
                                data-testid={`pdf-${aplicacao.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setSelectedAplicacao(aplicacao);
                                  setShowEnvio(true);
                                }}
                                title="Enviar Relatório"
                                data-testid={`enviar-${aplicacao.id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setEditAplicacao(aplicacao);
                                  setShowEditarAplicacao(true);
                                }}
                                title="Editar"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleExcluirAplicacao(aplicacao.id)}
                                title="Excluir"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Painel do Professor */}
        <TabsContent value="painel" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Resumo por Turma
                </CardTitle>
              </CardHeader>
              <CardContent>
                {turmas.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">Nenhuma turma cadastrada</p>
                ) : (
                  <div className="space-y-3">
                    {turmas.slice(0, 5).map(turma => {
                      const turmaAplicacoes = aplicacoes.filter(a => a.turma_id === turma.id);
                      const media = turmaAplicacoes.length > 0
                        ? Math.round(turmaAplicacoes.reduce((acc, a) => acc + (a.analise_ia?.score_fluencia || 0), 0) / turmaAplicacoes.length)
                        : 0;
                      
                      return (
                        <div key={turma.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{turma.name}</p>
                            <p className="text-sm text-muted-foreground">{turmaAplicacoes.length} aplicações</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{media}%</p>
                            <p className="text-xs text-muted-foreground">média fluência</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Ações Rápidas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => setShowNovaAplicacao(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Nova Aplicação
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={handleGerarTodosPDFs}
                  disabled={aplicacoesFiltradas.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Gerar PDFs da Turma
                </Button>
                <Button 
                  className="w-full justify-start" 
                  variant="outline"
                  onClick={() => {
                    setEnvioConfig(prev => ({ ...prev, tipo: 'turma' }));
                    setShowEnvio(true);
                  }}
                  disabled={aplicacoesFiltradas.length === 0}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar Relatórios para Famílias
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Dashboard de Evolução */}
        <TabsContent value="evolucao" className="space-y-4">
          {/* Filtros de Evolução */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label>Trimestre:</Label>
                  <Select value={selectedTrimestreEvolucao} onValueChange={setSelectedTrimestreEvolucao}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Todos os trimestres" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os trimestres</SelectItem>
                      <SelectItem value="1">1º Trimestre (28/01 - 08/05)</SelectItem>
                      <SelectItem value="2">2º Trimestre (11/05 - 28/08)</SelectItem>
                      <SelectItem value="3">3º Trimestre (31/08 - 17/12)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Label>Aluno:</Label>
                  <Select value={selectedStudentEvolucao} onValueChange={setSelectedStudentEvolucao}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Todos os alunos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os alunos</SelectItem>
                      {evolucaoData?.alunos?.map(aluno => (
                        <SelectItem key={aluno.student_id} value={aluno.student_id}>
                          {aluno.student_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={loadEvolucaoData}
                  disabled={loadingEvolucao}
                  className="ml-auto"
                >
                  {loadingEvolucao ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                  Atualizar
                </Button>
              </div>
            </CardContent>
          </Card>

          {loadingEvolucao ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : evolucaoData ? (
            <>
              {/* Estatísticas Gerais */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-purple-50 to-violet-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-purple-600" />
                      <div>
                        <p className="text-3xl font-bold text-purple-600">{evolucaoData.estatisticas?.total_alunos || 0}</p>
                        <p className="text-sm text-muted-foreground">Alunos Avaliados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-50 to-cyan-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="text-3xl font-bold text-blue-600">{evolucaoData.estatisticas?.total_avaliacoes || 0}</p>
                        <p className="text-sm text-muted-foreground">Avaliações</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="text-3xl font-bold text-green-600">{evolucaoData.estatisticas?.media_wcpm_turma || 0}</p>
                        <p className="text-sm text-muted-foreground">Média WCPM</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-orange-50 to-amber-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-8 h-8 text-orange-600" />
                      <div>
                        <p className="text-3xl font-bold text-orange-600">
                          {evolucaoData.estatisticas?.media_evolucao > 0 ? '+' : ''}{evolucaoData.estatisticas?.media_evolucao || 0}
                        </p>
                        <p className="text-sm text-muted-foreground">Evolução Média</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-pink-50 to-rose-50">
                  <CardContent className="pt-6">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium text-pink-700">Distribuição por Nível</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span>Crítico: {evolucaoData.estatisticas?.niveis?.critico || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                          <span>Desenv.: {evolucaoData.estatisticas?.niveis?.em_desenvolvimento || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          <span>Adequado: {evolucaoData.estatisticas?.niveis?.adequado || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span>Avançado: {evolucaoData.estatisticas?.niveis?.avancado || 0}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tabela de Evolução por Aluno */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Evolução de Fluência por Aluno
                  </CardTitle>
                  <CardDescription>
                    Acompanhe o progresso de WCPM de cada aluno ao longo do tempo
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {evolucaoData.alunos?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum dado de evolução encontrado</p>
                      <p className="text-sm mt-2">Registre aplicações e faça análises com a IA para ver a evolução</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Aluno</TableHead>
                          <TableHead>Turma</TableHead>
                          <TableHead className="text-center">Avaliações</TableHead>
                          <TableHead className="text-center">WCPM Inicial</TableHead>
                          <TableHead className="text-center">WCPM Atual</TableHead>
                          <TableHead className="text-center">Evolução</TableHead>
                          <TableHead className="text-center">Nível Atual</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {evolucaoData.alunos?.map(aluno => (
                          <TableRow key={aluno.student_id}>
                            <TableCell className="font-medium">{aluno.student_name}</TableCell>
                            <TableCell className="text-muted-foreground">{aluno.turma_name}</TableCell>
                            <TableCell className="text-center">{aluno.total_avaliacoes}</TableCell>
                            <TableCell className="text-center">{aluno.wcpm_inicial}</TableCell>
                            <TableCell className="text-center font-bold">{aluno.wcpm_atual}</TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold ${aluno.evolucao > 0 ? 'text-green-600' : aluno.evolucao < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {aluno.evolucao > 0 ? '+' : ''}{aluno.evolucao}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`${
                                aluno.nivel_atual === 1 ? 'bg-red-100 text-red-700' :
                                aluno.nivel_atual === 2 ? 'bg-yellow-100 text-yellow-700' :
                                aluno.nivel_atual === 3 ? 'bg-blue-100 text-blue-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {aluno.nivel_nome || 'N/A'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Gráfico de Barras de Níveis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      Distribuição por Nível de Fluência
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { nivel: 'Crítico (0-40)', valor: evolucaoData.estatisticas?.niveis?.critico || 0, cor: 'bg-red-500' },
                        { nivel: 'Em Desenvolvimento (41-70)', valor: evolucaoData.estatisticas?.niveis?.em_desenvolvimento || 0, cor: 'bg-yellow-500' },
                        { nivel: 'Adequado (71-100)', valor: evolucaoData.estatisticas?.niveis?.adequado || 0, cor: 'bg-blue-500' },
                        { nivel: 'Avançado (101+)', valor: evolucaoData.estatisticas?.niveis?.avancado || 0, cor: 'bg-green-500' },
                      ].map(item => {
                        const total = evolucaoData.estatisticas?.total_alunos || 1;
                        const pct = Math.round((item.valor / total) * 100);
                        return (
                          <div key={item.nivel} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{item.nivel}</span>
                              <span className="font-medium">{item.valor} alunos ({pct}%)</span>
                            </div>
                            <div className="h-3 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${item.cor} transition-all duration-500`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-600" />
                      Referência de Trimestres
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { nome: '1º Trimestre', inicio: '28/01/2026', fim: '08/05/2026', dias: 67 },
                        { nome: '2º Trimestre', inicio: '11/05/2026', fim: '28/08/2026', dias: 61 },
                        { nome: '3º Trimestre', inicio: '31/08/2026', fim: '17/12/2026', dias: 72 },
                      ].map((trim, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{trim.nome}</p>
                            <p className="text-sm text-muted-foreground">{trim.inicio} - {trim.fim}</p>
                          </div>
                          <Badge variant="outline">{trim.dias} dias letivos</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Selecione uma turma para ver a evolução</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Nova Aplicação */}
      <Dialog open={showNovaAplicacao} onOpenChange={setShowNovaAplicacao}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Aplicação de Leitura</DialogTitle>
            <DialogDescription>
              Registre uma nova avaliação de fluência de leitura
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Turma *</Label>
                <Select 
                  value={novaAplicacao.turma_id} 
                  onValueChange={(value) => {
                    const turma = turmas.find(t => t.id === value);
                    setNovaAplicacao(prev => ({ 
                      ...prev, 
                      turma_id: value,
                      turma_name: turma?.name || '',
                      student_id: '',
                      student_name: '',
                      selecionar_todos: false
                    }));
                    loadStudentsByTurma(value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a turma" />
                  </SelectTrigger>
                  <SelectContent>
                    {turmas.map(turma => (
                      <SelectItem key={turma.id} value={turma.id}>{turma.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Aluno</Label>
                {novaAplicacao.turma_id && (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="selecionar_todos"
                      checked={novaAplicacao.selecionar_todos}
                      onChange={(e) => setNovaAplicacao(prev => ({ 
                        ...prev, 
                        selecionar_todos: e.target.checked,
                        student_id: '',
                        student_name: ''
                      }))}
                      className="rounded"
                    />
                    <Label htmlFor="selecionar_todos" className="cursor-pointer text-sm flex items-center gap-1">
                      <UsersRound className="w-4 h-4" />
                      Todos os alunos da turma ({students.length})
                    </Label>
                  </div>
                )}
                <Select 
                  value={novaAplicacao.student_id} 
                  onValueChange={(value) => {
                    const student = students.find(s => s.id === value);
                    setNovaAplicacao(prev => ({ 
                      ...prev, 
                      student_id: value,
                      student_name: student?.name || student?.full_name || '',
                      selecionar_todos: false
                    }));
                  }}
                  disabled={novaAplicacao.selecionar_todos || !novaAplicacao.turma_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={novaAplicacao.selecionar_todos ? "Todos selecionados" : "Selecione o aluno"} />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map(student => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name || student.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Idioma</Label>
                <Select 
                  value={novaAplicacao.idioma} 
                  onValueChange={(value) => setNovaAplicacao(prev => ({ ...prev, idioma: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portugues">🇧🇷 Português</SelectItem>
                    <SelectItem value="ingles">🇺🇸 Inglês</SelectItem>
                    <SelectItem value="espanhol">🇪🇸 Espanhol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Data da Aplicação</Label>
                <Input 
                  type="date" 
                  value={novaAplicacao.data_aplicacao}
                  onChange={(e) => setNovaAplicacao(prev => ({ ...prev, data_aplicacao: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tempo de Leitura</Label>
                <Input 
                  placeholder="Ex: 2:30"
                  value={novaAplicacao.tempo_leitura}
                  onChange={(e) => setNovaAplicacao(prev => ({ ...prev, tempo_leitura: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Texto Original *</Label>
              <Textarea 
                placeholder="Cole aqui o texto que o aluno deveria ler..."
                rows={4}
                value={novaAplicacao.texto_original}
                onChange={(e) => setNovaAplicacao(prev => ({ ...prev, texto_original: e.target.value }))}
              />
            </div>

            {/* Seção de Áudio para Transcrição IA */}
            <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <Label className="flex items-center gap-2 text-purple-700 font-medium">
                <Mic className="w-4 h-4" />
                Áudio da Leitura (para transcrição pela IA)
              </Label>
              <p className="text-xs text-muted-foreground">
                Faça upload do arquivo de áudio ou cole um link. A IA irá transcrever e analisar automaticamente.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Upload de arquivo */}
                <div className="space-y-2">
                  <input
                    ref={audioInputRef}
                    type="file"
                    accept="audio/*,.mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                    onChange={handleTranscreverAudio}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => audioInputRef.current?.click()}
                    disabled={transcribing}
                    className="w-full text-purple-600 border-purple-300 hover:bg-purple-100"
                  >
                    {transcribing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {transcribing ? 'Transcrevendo...' : 'Upload Arquivo de Áudio'}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    MP3, WAV, M4A (máx 25MB)
                  </p>
                </div>
                
                {/* Link de áudio */}
                <div className="space-y-2">
                  <Input 
                    placeholder="https://drive.google.com/..."
                    value={novaAplicacao.audio_url}
                    onChange={(e) => setNovaAplicacao(prev => ({ ...prev, audio_url: e.target.value }))}
                  />
                  <p className="text-xs text-center text-muted-foreground">
                    Ou cole um link (Google Drive, etc.)
                  </p>
                </div>
              </div>
              
              {/* Texto transcrito (readonly) */}
              {novaAplicacao.texto_lido && (
                <div className="space-y-2 mt-3">
                  <Label className="text-sm text-green-700 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Texto Transcrito pela IA:
                  </Label>
                  <div className="p-3 bg-white rounded border text-sm max-h-32 overflow-y-auto">
                    {novaAplicacao.texto_lido}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaAplicacao(false)}>
              Cancelar
            </Button>
            <Button onClick={handleNovaAplicacao} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {novaAplicacao.selecionar_todos ? `Salvar para ${students.length} alunos` : 'Salvar Aplicação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Análise IA */}
      <Dialog open={showAnalise} onOpenChange={setShowAnalise}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Análise de Fluência por IA
            </DialogTitle>
            <DialogDescription>
              {selectedAplicacao?.student_name} - {getIdiomaLabel(selectedAplicacao?.idioma)}
            </DialogDescription>
          </DialogHeader>
          
          {analyzing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-4" />
              <p className="text-lg font-medium">Analisando com Inteligência Artificial...</p>
              <p className="text-muted-foreground">Aguarde enquanto processamos a leitura</p>
            </div>
          ) : analiseResult ? (
            <div className="space-y-6">
              {/* Nível de Fluência e WCPM */}
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl">
                <div className="text-center flex-1">
                  <div className="text-5xl font-bold text-purple-600">
                    {analiseResult.metricas?.wcpm || analiseResult.ppm || 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">WCPM</div>
                </div>
                <div className="text-center flex-1 border-l border-purple-200 pl-6">
                  <div className="text-2xl font-bold">
                    Nível {analiseResult.metricas?.nivel_fluencia || 3}
                  </div>
                  <div className={`text-sm px-3 py-1 rounded-full inline-block mt-1 ${
                    WCPM_NIVEIS[analiseResult.metricas?.nivel_fluencia || 3]?.cor || 'bg-blue-500'
                  } text-white`}>
                    {analiseResult.metricas?.nivel_nome || 'Adequado'}
                  </div>
                </div>
                <div className="text-center flex-1 border-l border-purple-200 pl-6">
                  <div className="text-5xl font-bold text-green-600">
                    {analiseResult.metricas?.precisao || analiseResult.score_fluencia || 0}%
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">Precisão</div>
                </div>
              </div>

              {/* Métricas Detalhadas */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-blue-600">{analiseResult.metricas?.tempo_segundos || 60}s</p>
                  <p className="text-xs text-muted-foreground">Tempo</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-green-600">{analiseResult.metricas?.itens_certos || 0}</p>
                  <p className="text-xs text-muted-foreground">Itens Certos</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-red-600">{analiseResult.metricas?.itens_errados || 0}</p>
                  <p className="text-xs text-muted-foreground">Erros</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-purple-600">{analiseResult.metricas?.qtde_itens || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Itens</p>
                </div>
              </div>

              {/* Registro Técnico - 5 Seções */}
              {analiseResult.registro_tecnico && (
                <div className="space-y-4">
                  <h3 className="font-bold text-lg border-b pb-2">📋 Registro Técnico</h3>
                  
                  {/* 1. Precisão */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">1️⃣ Precisão</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {analiseResult.registro_tecnico.precisao?.trocas?.length > 0 && (
                        <div>
                          <p className="font-medium text-red-600">Trocas:</p>
                          <ul className="list-disc list-inside">
                            {analiseResult.registro_tecnico.precisao.trocas.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {analiseResult.registro_tecnico.precisao?.omissoes?.length > 0 && (
                        <div>
                          <p className="font-medium text-orange-600">Omissões:</p>
                          <ul className="list-disc list-inside">
                            {analiseResult.registro_tecnico.precisao.omissoes.map((o, i) => (
                              <li key={i}>{o}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {analiseResult.registro_tecnico.precisao?.autocorrecoes?.length > 0 && (
                        <div>
                          <p className="font-medium text-green-600">Autocorreções:</p>
                          <ul className="list-disc list-inside">
                            {analiseResult.registro_tecnico.precisao.autocorrecoes.map((a, i) => (
                              <li key={i}>{a}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 2. Ritmo */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold mb-2">2️⃣ Ritmo</h4>
                    <p className="text-sm">
                      <strong>Avaliação:</strong> {analiseResult.registro_tecnico.ritmo?.avaliacao || 'N/A'} | 
                      <strong> Pausas:</strong> {analiseResult.registro_tecnico.ritmo?.pausas_inadequadas || 0} | 
                      <strong> Hesitações:</strong> {analiseResult.registro_tecnico.ritmo?.hesitacoes || 0}
                    </p>
                    <p className="text-sm mt-2">{analiseResult.registro_tecnico.ritmo?.observacoes}</p>
                  </div>

                  {/* 3. Prosódia */}
                  <div className="p-4 bg-violet-50 rounded-lg">
                    <h4 className="font-semibold mb-2">3️⃣ Prosódia</h4>
                    <p className="text-sm">
                      <strong>Entonação:</strong> {analiseResult.registro_tecnico.prosodia?.entonacao || 'N/A'} | 
                      <strong> Pontuação:</strong> {analiseResult.registro_tecnico.prosodia?.respeito_pontuacao || 'N/A'} | 
                      <strong> Expressividade:</strong> {analiseResult.registro_tecnico.prosodia?.expressividade || 'N/A'}
                    </p>
                    <p className="text-sm mt-2">{analiseResult.registro_tecnico.prosodia?.observacoes}</p>
                  </div>

                  {/* 4. Hipótese de Nível */}
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-semibold mb-2">4️⃣ Hipótese de Nível de Fluência</h4>
                    <p className="text-sm">
                      <strong>Nível {analiseResult.registro_tecnico.hipotese_nivel?.nivel}:</strong>{' '}
                      {analiseResult.registro_tecnico.hipotese_nivel?.justificativa}
                    </p>
                  </div>

                  {/* 5. Intervenções */}
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold mb-2">5️⃣ Sugestão de Intervenções (próximos 15 dias)</h4>
                    <ul className="text-sm space-y-1">
                      {analiseResult.registro_tecnico.intervencoes_15_dias?.map((int, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-green-600">•</span>
                          <span>{int}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Resumo Técnico */}
                  <div className="p-4 bg-gray-100 rounded-lg">
                    <h4 className="font-semibold mb-2">📝 Resumo</h4>
                    <p className="text-sm">{analiseResult.registro_tecnico.resumo_tecnico}</p>
                  </div>
                </div>
              )}

              {/* Mensagem para a Família */}
              {analiseResult.relatorio_familia && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold mb-2 text-blue-700">👨‍👩‍👧 Mensagem para a Família</h4>
                  <p className="text-sm whitespace-pre-wrap">{analiseResult.relatorio_familia.mensagem_personalizada}</p>
                  {analiseResult.relatorio_familia.sugestoes_casa?.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium text-sm">Sugestões para casa:</p>
                      <ul className="list-disc list-inside text-sm">
                        {analiseResult.relatorio_familia.sugestoes_casa.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Não foi possível realizar a análise</p>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowAnalise(false)}>
              Fechar
            </Button>
            {analiseResult && (
              <>
                <Button onClick={handleSalvarAnalise}>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Análise
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleSaveToDrive(selectedAplicacao?.id)}
                  disabled={savingToDrive}
                  className="bg-blue-50 border-blue-200 hover:bg-blue-100"
                >
                  {savingToDrive ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CloudUpload className="w-4 h-4 mr-2 text-blue-600" />
                  )}
                  Salvar no Drive
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Registro Técnico (Visualização) */}
      <Dialog open={showRegistroTecnico} onOpenChange={setShowRegistroTecnico}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Registro Técnico - {selectedAplicacao?.student_name}
            </DialogTitle>
            <DialogDescription>
              Análise técnica gerada pela IA
            </DialogDescription>
          </DialogHeader>
          
          {selectedAplicacao?.analise_ia?.registro_tecnico ? (
            <div className="space-y-4">
              {/* 1. Precisão */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2">1️⃣ Precisão</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedAplicacao.analise_ia.registro_tecnico.precisao?.trocas?.length > 0 && (
                    <div>
                      <p className="font-medium text-red-600">Trocas:</p>
                      <ul className="list-disc list-inside">
                        {selectedAplicacao.analise_ia.registro_tecnico.precisao.trocas.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedAplicacao.analise_ia.registro_tecnico.precisao?.omissoes?.length > 0 && (
                    <div>
                      <p className="font-medium text-orange-600">Omissões:</p>
                      <ul className="list-disc list-inside">
                        {selectedAplicacao.analise_ia.registro_tecnico.precisao.omissoes.map((o, i) => (
                          <li key={i}>{o}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* 2. Ritmo */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-2">2️⃣ Ritmo</h4>
                <p className="text-sm">
                  <strong>Avaliação:</strong> {selectedAplicacao.analise_ia.registro_tecnico.ritmo?.avaliacao || 'N/A'}
                </p>
                <p className="text-sm mt-2">{selectedAplicacao.analise_ia.registro_tecnico.ritmo?.observacoes}</p>
              </div>

              {/* 3. Prosódia */}
              <div className="p-4 bg-violet-50 rounded-lg">
                <h4 className="font-semibold mb-2">3️⃣ Prosódia</h4>
                <p className="text-sm">
                  <strong>Entonação:</strong> {selectedAplicacao.analise_ia.registro_tecnico.prosodia?.entonacao || 'N/A'}
                </p>
                <p className="text-sm mt-2">{selectedAplicacao.analise_ia.registro_tecnico.prosodia?.observacoes}</p>
              </div>

              {/* 4. Hipótese */}
              <div className="p-4 bg-amber-50 rounded-lg">
                <h4 className="font-semibold mb-2">4️⃣ Hipótese de Nível</h4>
                <p className="text-sm">{selectedAplicacao.analise_ia.registro_tecnico.hipotese_nivel?.justificativa}</p>
              </div>

              {/* 5. Intervenções */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold mb-2">5️⃣ Intervenções (15 dias)</h4>
                <ul className="text-sm space-y-1">
                  {selectedAplicacao.analise_ia.registro_tecnico.intervencoes_15_dias?.map((int, i) => (
                    <li key={i}>• {int}</li>
                  ))}
                </ul>
              </div>

              {/* Resumo */}
              <div className="p-4 bg-gray-100 rounded-lg">
                <h4 className="font-semibold mb-2">📝 Resumo</h4>
                <p className="text-sm">{selectedAplicacao.analise_ia.registro_tecnico.resumo_tecnico}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Análise não realizada. Clique no botão de análise (🧠) primeiro.</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegistroTecnico(false)}>
              Fechar
            </Button>
            <Button onClick={() => handleGerarPDF(selectedAplicacao)} disabled={!selectedAplicacao?.analise_ia}>
              <Download className="w-4 h-4 mr-2" />
              Gerar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Envio */}
      <Dialog open={showEnvio} onOpenChange={setShowEnvio}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Enviar Relatórios
            </DialogTitle>
            <DialogDescription>
              Envie os relatórios de fluência para as famílias
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Envio</Label>
              <Select 
                value={envioConfig.tipo} 
                onValueChange={(value) => setEnvioConfig(prev => ({ ...prev, tipo: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Individual (apenas este aluno)
                    </div>
                  </SelectItem>
                  <SelectItem value="turma">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Toda a Turma
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Método de Envio</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant={envioConfig.metodo === 'email' ? 'default' : 'outline'}
                  onClick={() => setEnvioConfig(prev => ({ ...prev, metodo: 'email' }))}
                  className="justify-start"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button 
                  variant={envioConfig.metodo === 'whatsapp' ? 'default' : 'outline'}
                  onClick={() => setEnvioConfig(prev => ({ ...prev, metodo: 'whatsapp' }))}
                  className="justify-start"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {envioConfig.tipo === 'individual' && selectedAplicacao && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm"><strong>Aluno:</strong> {selectedAplicacao.student_name}</p>
                <p className="text-sm"><strong>Data:</strong> {new Date(selectedAplicacao.data_aplicacao).toLocaleDateString('pt-BR')}</p>
              </div>
            )}

            {envioConfig.tipo === 'turma' && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">
                  <strong>Turma:</strong> {turmas.find(t => t.id === selectedTurma)?.name || 'Todas'}
                </p>
                <p className="text-sm">
                  <strong>Total de relatórios:</strong> {aplicacoesFiltradas.length}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnvio(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEnviar} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar {envioConfig.metodo === 'email' ? 'por Email' : 'via WhatsApp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Configurações */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurações do DNA.IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Chave API OpenAI (ChatGPT)</Label>
              <Input 
                type="password"
                placeholder="sk-..."
                value={apiKeyConfig}
                onChange={(e) => setApiKeyConfig(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Insira sua chave API do OpenAI para habilitar a análise por IA
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSalvarApiKey}>
              <Save className="w-4 h-4 mr-2" />
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Upload Áudios por Turma */}
      <Dialog open={showUploadTurma} onOpenChange={setShowUploadTurma}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-purple-600" />
              Upload de Áudios por Turma
            </DialogTitle>
            <DialogDescription>
              Envie múltiplos arquivos de áudio. O nome do arquivo deve ser o nome do aluno (ex: "joao_silva.mp3")
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Turma *</Label>
                <Select 
                  value={uploadTurmaConfig.turma_id} 
                  onValueChange={(value) => setUploadTurmaConfig(prev => ({ ...prev, turma_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a turma" />
                  </SelectTrigger>
                  <SelectContent>
                    {turmas.map(turma => (
                      <SelectItem key={turma.id} value={turma.id}>{turma.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Idioma</Label>
                <Select 
                  value={uploadTurmaConfig.idioma} 
                  onValueChange={(value) => setUploadTurmaConfig(prev => ({ ...prev, idioma: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portugues">🇧🇷 Português</SelectItem>
                    <SelectItem value="ingles">🇺🇸 Inglês</SelectItem>
                    <SelectItem value="espanhol">🇪🇸 Espanhol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Data da Aplicação</Label>
              <Input 
                type="date" 
                value={uploadTurmaConfig.data_aplicacao}
                onChange={(e) => setUploadTurmaConfig(prev => ({ ...prev, data_aplicacao: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Texto Original (que todos deveriam ler) *</Label>
              <Textarea 
                placeholder="Cole aqui o texto que foi usado para a avaliação..."
                rows={4}
                value={uploadTurmaConfig.texto_original}
                onChange={(e) => setUploadTurmaConfig(prev => ({ ...prev, texto_original: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Arquivos de Áudio</Label>
              <input
                ref={audioInputTurmaRef}
                type="file"
                accept="audio/*"
                multiple
                onChange={handleUploadAudiosTurma}
                className="hidden"
              />
              <div 
                onClick={() => audioInputTurmaRef.current?.click()}
                className="border-2 border-dashed border-purple-200 rounded-lg p-8 text-center cursor-pointer hover:bg-purple-50 transition-colors"
              >
                <Upload className="w-12 h-12 mx-auto text-purple-400 mb-4" />
                <p className="text-sm font-medium">Clique para selecionar os arquivos de áudio</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Formatos: MP3, WAV, M4A, etc. | Nome do arquivo = Nome do aluno
                </p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>💡 Dica:</strong> Nomeie os arquivos com o nome do aluno para identificação automática. 
                Exemplo: "maria_silva.mp3", "joao-santos.wav"
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadTurma(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => audioInputTurmaRef.current?.click()}
              disabled={loading || !uploadTurmaConfig.turma_id || !uploadTurmaConfig.texto_original}
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {loading ? 'Processando...' : 'Selecionar e Enviar Áudios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Aplicação */}
      <Dialog open={showEditarAplicacao} onOpenChange={setShowEditarAplicacao}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Aplicação</DialogTitle>
            <DialogDescription>
              Editar dados da aplicação de {editAplicacao?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          {editAplicacao && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Aluno</Label>
                  <Input 
                    value={editAplicacao.student_name || ''}
                    onChange={(e) => setEditAplicacao(prev => ({ ...prev, student_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Turma</Label>
                  <Input 
                    value={editAplicacao.turma_name || ''}
                    onChange={(e) => setEditAplicacao(prev => ({ ...prev, turma_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Idioma</Label>
                  <Select 
                    value={editAplicacao.idioma || 'portugues'} 
                    onValueChange={(value) => setEditAplicacao(prev => ({ ...prev, idioma: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portugues">🇧🇷 Português</SelectItem>
                      <SelectItem value="ingles">🇺🇸 Inglês</SelectItem>
                      <SelectItem value="espanhol">🇪🇸 Espanhol</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input 
                    type="date" 
                    value={editAplicacao.data_aplicacao || ''}
                    onChange={(e) => setEditAplicacao(prev => ({ ...prev, data_aplicacao: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tempo de Leitura</Label>
                  <Input 
                    placeholder="Ex: 1:30"
                    value={editAplicacao.tempo_leitura || ''}
                    onChange={(e) => setEditAplicacao(prev => ({ ...prev, tempo_leitura: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Texto Original</Label>
                <Textarea 
                  rows={4}
                  value={editAplicacao.texto_original || ''}
                  onChange={(e) => setEditAplicacao(prev => ({ ...prev, texto_original: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Texto Lido pelo Aluno</Label>
                <Textarea 
                  rows={4}
                  value={editAplicacao.texto_lido || ''}
                  onChange={(e) => setEditAplicacao(prev => ({ ...prev, texto_lido: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Link do Áudio</Label>
                <Input 
                  placeholder="https://..."
                  value={editAplicacao.audio_url || ''}
                  onChange={(e) => setEditAplicacao(prev => ({ ...prev, audio_url: e.target.value }))}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarAplicacao(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                handleExcluirAplicacao(editAplicacao?.id);
                setShowEditarAplicacao(false);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
            <Button onClick={handleEditarAplicacao} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Criar Texto com IA */}
      <Dialog open={showCriarTexto} onOpenChange={setShowCriarTexto}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Criar Texto com IA
            </DialogTitle>
            <DialogDescription>
              Gere textos adequados para avaliação de fluência de leitura baseado nos parâmetros WCPM
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ano Escolar *</Label>
                <Select 
                  value={criarTextoConfig.ano_escolar} 
                  onValueChange={(value) => setCriarTextoConfig(prev => ({ ...prev, ano_escolar: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o ano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1º Ano (EF)</SelectItem>
                    <SelectItem value="2">2º Ano (EF)</SelectItem>
                    <SelectItem value="3">3º Ano (EF)</SelectItem>
                    <SelectItem value="4">4º Ano (EF)</SelectItem>
                    <SelectItem value="5">5º Ano (EF)</SelectItem>
                    <SelectItem value="6">6º Ano (EF)</SelectItem>
                    <SelectItem value="7">7º Ano (EF)</SelectItem>
                    <SelectItem value="8">8º Ano (EF)</SelectItem>
                    <SelectItem value="9">9º Ano (EF)</SelectItem>
                    <SelectItem value="1em">1º Ano (EM)</SelectItem>
                    <SelectItem value="2em">2º Ano (EM)</SelectItem>
                    <SelectItem value="3em">3º Ano (EM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Período do Ano *</Label>
                <Select 
                  value={criarTextoConfig.periodo} 
                  onValueChange={(value) => setCriarTextoConfig(prev => ({ ...prev, periodo: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inicio">Início do Ano</SelectItem>
                    <SelectItem value="meio">Meio do Ano</SelectItem>
                    <SelectItem value="fim">Fim do Ano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gênero Textual</Label>
                <Select 
                  value={criarTextoConfig.genero_textual} 
                  onValueChange={(value) => setCriarTextoConfig(prev => ({ ...prev, genero_textual: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="narrativo">📖 Narrativo (História)</SelectItem>
                    <SelectItem value="fabula">🦊 Fábula (com moral)</SelectItem>
                    <SelectItem value="informativo">📰 Informativo</SelectItem>
                    <SelectItem value="descritivo">🎨 Descritivo</SelectItem>
                    <SelectItem value="poesia">🎭 Poesia</SelectItem>
                    <SelectItem value="instrucional">📋 Instrucional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Idioma</Label>
                <Select 
                  value={criarTextoConfig.idioma} 
                  onValueChange={(value) => setCriarTextoConfig(prev => ({ ...prev, idioma: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portugues">🇧🇷 Português</SelectItem>
                    <SelectItem value="ingles">🇺🇸 Inglês</SelectItem>
                    <SelectItem value="espanhol">🇪🇸 Espanhol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tema (opcional)</Label>
              <Input 
                placeholder="Ex: animais da floresta, férias na praia, amizade..."
                value={criarTextoConfig.tema}
                onChange={(e) => setCriarTextoConfig(prev => ({ ...prev, tema: e.target.value }))}
              />
            </div>

            {/* Informação de WCPM */}
            {criarTextoConfig.ano_escolar && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm font-medium text-purple-700 mb-2">Meta de WCPM para este nível:</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { periodo: 'inicio', label: 'Início', valores: { '1': 20, '2': 50, '3': 70, '4': 90, '5': 110, '6': 120, '7': 130, '8': 140, '9': 150, '1em': 160, '2em': 175, '3em': 185 } },
                    { periodo: 'meio', label: 'Meio', valores: { '1': 40, '2': 70, '3': 90, '4': 110, '5': 130, '6': 140, '7': 150, '8': 160, '9': 170, '1em': 175, '2em': 185, '3em': 195 } },
                    { periodo: 'fim', label: 'Fim', valores: { '1': 60, '2': 90, '3': 110, '4': 130, '5': 150, '6': 160, '7': 170, '8': 180, '9': 190, '1em': 190, '2em': 200, '3em': 210 } },
                  ].map(item => (
                    <div 
                      key={item.periodo} 
                      className={`p-2 rounded ${criarTextoConfig.periodo === item.periodo ? 'bg-purple-200' : 'bg-white'}`}
                    >
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                      <p className="text-lg font-bold text-purple-600">
                        {item.valores[criarTextoConfig.ano_escolar] || '-'}
                      </p>
                      <p className="text-xs text-muted-foreground">palavras</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  O texto gerado terá aproximadamente essa quantidade de palavras para leitura em 60 segundos.
                </p>
              </div>
            )}

            <Button 
              onClick={handleGerarTexto} 
              disabled={gerandoTexto || !criarTextoConfig.ano_escolar}
              className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
            >
              {gerandoTexto ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando texto com IA...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Gerar Texto
                </>
              )}
            </Button>

            {/* Texto Gerado */}
            {textoGerado && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-green-700 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Texto Gerado ({textoGerado.palavras} palavras)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{textoGerado.ano_escolar}</Badge>
                    <Badge variant="outline">{textoGerado.periodo}</Badge>
                    <Badge variant="outline">Meta: {textoGerado.wcpm_alvo} WCPM</Badge>
                  </div>
                </div>
                <div className="p-4 bg-white rounded-lg border max-h-48 overflow-y-auto">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{textoGerado.texto}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={handleCopiarTexto}>
                    <FileText className="w-4 h-4 mr-2" />
                    Copiar Texto
                  </Button>
                  <Button variant="outline" onClick={handleImprimirTexto}>
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimir
                  </Button>
                  <Button variant="outline" onClick={handleEnviarTextoEmail} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                    Enviar por Email
                  </Button>
                  <Button onClick={handleUsarTextoGerado} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Usar em Nova Aplicação
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCriarTexto(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DnaIA;
